import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import {
  useCreatePettyCashTopup,
  useCreatePettyCashVoucher,
  usePettyCashBalance,
  usePettyCashReconcile,
  usePettyCashSettlement,
  usePettyCashTopups,
} from '../hooks/useOps';
import { opsApi } from '../api/client';
import type { ExpenseCategory, PettyCashTopup } from '../api/types';
import { formatDate, formatIDR, humanizeStatus } from '../lib/format';

const CATEGORIES: ExpenseCategory[] = ['infra', 'saas', 'ai_api', 'contractor', 'office', 'tax', 'other'];

function firstOfMonth(): string {
  return new Date().toISOString().slice(0, 8) + '01';
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PettyCashPage() {
  const { data: balance } = usePettyCashBalance();
  const { data: topups, isLoading, error } = usePettyCashTopups();
  const createTopup = useCreatePettyCashTopup();
  const createVoucher = useCreatePettyCashVoucher();
  const reconcile = usePettyCashReconcile();

  // Settlement range (defaults to the running month).
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const { data: settlement } = usePettyCashSettlement(from, to);

  // Top-up dialog.
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(0);
  const [custodian, setCustodian] = useState('Haikal');
  const [transferRef, setTransferRef] = useState('');
  const [toppedUpAt, setToppedUpAt] = useState(today());

  // Voucher dialog — the photo is REQUIRED (no photo, no voucher).
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [vCategory, setVCategory] = useState<ExpenseCategory>('office');
  const [vDescription, setVDescription] = useState('');
  const [vAmount, setVAmount] = useState(0);
  const [vDate, setVDate] = useState(today());
  const [vVendor, setVVendor] = useState('');
  const [vFile, setVFile] = useState<File | null>(null);

  // Reconcile dialog.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [counted, setCounted] = useState(0);
  const [reconcileNote, setReconcileNote] = useState('');
  const [reconcileResult, setReconcileResult] = useState<string | null>(null);

  const [formError, setFormError] = useState<string | null>(null);

  const submitTopup = async () => {
    try {
      await createTopup.mutateAsync({
        amount: topupAmount,
        custodian,
        transferRef: transferRef || undefined,
        toppedUpAt,
      });
      setTopupOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const submitVoucher = async () => {
    if (!vFile) return;
    try {
      await createVoucher.mutateAsync({
        fields: {
          category: vCategory,
          description: vDescription,
          amount: vAmount,
          expenseDate: vDate,
          ...(vVendor ? { vendorName: vVendor } : {}),
        },
        receipt: vFile,
      });
      setVoucherOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const submitReconcile = async () => {
    try {
      const res = await reconcile.mutateAsync({
        countedAmount: counted,
        countedAt: today(),
        notes: reconcileNote || undefined,
      });
      setReconcileResult(
        res.posted
          ? `Selisih ${formatIDR(res.variance)} dibukukan sebagai beban (buku sekarang = fisik).`
          : (res.note ?? `Selisih ${formatIDR(res.variance)}.`),
      );
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const downloadPdf = async () => {
    const blob = await opsApi.pettyCashSettlementPdf(from, to);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kas-kecil-${from}-${to}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const topupColumns: GridColDef<PettyCashTopup>[] = [
    { field: 'toppedUpAt', headerName: 'Date', width: 120, valueGetter: (v) => formatDate(v as string) },
    { field: 'custodian', headerName: 'Custodian', width: 140 },
    { field: 'transferRef', headerName: 'Transfer ref', flex: 1, valueGetter: (v) => v ?? '—' },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Petty cash"
        action={
          <Stack direction="row" gap={1}>
            <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => { setReconcileResult(null); setReconcileOpen(true); }}>
              Reconcile
            </Button>
            <Button variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => { setFormError(null); setVoucherOpen(true); }}>
              Log voucher
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormError(null); setTopupOpen(true); }}>
              Top-up
            </Button>
          </Stack>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mb={2}>
        <Card variant="outlined" sx={{ minWidth: 260 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Fund balance (GL 1120)
            </Typography>
            <Typography variant="h4">{formatIDR(balance?.balance ?? '0')}</Typography>
            <Typography variant="caption" color="text.secondary">
              What the custodian should currently hold
            </Typography>
          </CardContent>
        </Card>
        {settlement && (
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">
                  Settlement {formatDate(from)} – {formatDate(to)}
                </Typography>
                <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>
                  PDF
                </Button>
              </Stack>
              <Stack direction="row" gap={4} mt={1} flexWrap="wrap">
                <div>
                  <Typography variant="caption" color="text.secondary">Opening</Typography>
                  <Typography>{formatIDR(settlement.openingBalance)}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Top-ups</Typography>
                  <Typography>{formatIDR(settlement.topupTotal)}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Vouchers</Typography>
                  <Typography>({formatIDR(settlement.voucherTotal)})</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Closing</Typography>
                  <Typography>{formatIDR(settlement.closingBalance)}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Replenish (imprest)</Typography>
                  <Typography fontWeight={600}>{formatIDR(settlement.suggestedReplenishment)}</Typography>
                </div>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Stack direction="row" gap={2} mb={2}>
        <TextField
          label="Settlement from"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="to"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {settlement && settlement.categories.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Vouchers by category
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settlement.categories.map((c) => (
                <>
                  <TableRow key={c.category} sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                      {humanizeStatus(c.category)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatIDR(c.total)}
                    </TableCell>
                  </TableRow>
                  {c.vouchers.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{formatDate(v.expenseDate)}</TableCell>
                      <TableCell>{v.description}</TableCell>
                      <TableCell>{v.vendorName ?? '—'}</TableCell>
                      <TableCell align="right">{formatIDR(v.amount)}</TableCell>
                    </TableRow>
                  ))}
                </>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="subtitle2" gutterBottom>
        Top-ups
      </Typography>
      <Paper variant="outlined" sx={{ height: 360 }}>
        <DataGrid
          rows={topups ?? []}
          columns={topupColumns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      {/* Top-up dialog */}
      <Dialog open={topupOpen} onClose={() => setTopupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Petty-cash top-up</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              label="Amount"
              type="number"
              value={topupAmount}
              onChange={(e) => setTopupAmount(Number(e.target.value))}
            />
            <TextField label="Custodian" value={custodian} onChange={(e) => setCustodian(e.target.value)} />
            <TextField
              label="Transfer reference"
              value={transferRef}
              onChange={(e) => setTransferRef(e.target.value)}
            />
            <TextField
              label="Date"
              type="date"
              value={toppedUpAt}
              onChange={(e) => setToppedUpAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitTopup}
            disabled={topupAmount <= 0 || !custodian.trim() || createTopup.isPending}
          >
            Record top-up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voucher dialog — photo REQUIRED */}
      <Dialog open={voucherOpen} onClose={() => setVoucherOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log petty-cash voucher</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Alert severity="info">
              The receipt photo (nota belanja) is required — no photo, no voucher. Past dates are
              fine (batch reporting).
            </Alert>
            <Stack direction="row" gap={2}>
              <TextField
                select
                label="Category"
                value={vCategory}
                onChange={(e) => setVCategory(e.target.value as ExpenseCategory)}
                sx={{ flex: 1 }}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {humanizeStatus(c)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Date"
                type="date"
                value={vDate}
                onChange={(e) => setVDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Description"
              value={vDescription}
              onChange={(e) => setVDescription(e.target.value)}
              required
            />
            <Stack direction="row" gap={2}>
              <TextField
                label="Amount"
                type="number"
                value={vAmount}
                onChange={(e) => setVAmount(Number(e.target.value))}
                sx={{ flex: 1 }}
              />
              <TextField label="Vendor" value={vVendor} onChange={(e) => setVVendor(e.target.value)} sx={{ flex: 1 }} />
            </Stack>
            <Button variant="outlined" component="label">
              {vFile ? `Receipt: ${vFile.name}` : 'Attach receipt photo (required)'}
              <input
                hidden
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setVFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoucherOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitVoucher}
            disabled={!vDescription.trim() || vAmount <= 0 || !vFile || createVoucher.isPending}
          >
            Log voucher
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile dialog */}
      <Dialog open={reconcileOpen} onClose={() => setReconcileOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reconcile physical count</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Book balance: {formatIDR(balance?.balance ?? '0')}. A shortage posts as an expense
              line; an overage needs a manual journal.
            </Typography>
            <TextField
              label="Physical cash counted"
              type="number"
              value={counted}
              onChange={(e) => setCounted(Number(e.target.value))}
            />
            <TextField
              label="Notes"
              value={reconcileNote}
              onChange={(e) => setReconcileNote(e.target.value)}
            />
            {reconcileResult && <Alert severity="info">{reconcileResult}</Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileOpen(false)}>Close</Button>
          <Button variant="contained" onClick={submitReconcile} disabled={reconcile.isPending}>
            Reconcile
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
